/**
 * Derives Candid-eligible order context and opens the Trigger Candid modals.
 *
 * @module useCandidUploadActions
 */
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { groupJCodesByAuthorization } from "../../../../../components/modals/authLetterUploadModal/candidAuthorizationPayload";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../../../enums/modalId";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { CANDID_ELIGIBLE_AUTH_STATUSES } from "../../../../singleOrderLayout/components/header/components/candidUploadButton/useCandidUploadButton";
import { useSingleOrderUnwrappedData } from "../../../../singleOrderLayout/hooks/useSingleOrderUnwrappedData";
import { getAllJCodesFromAuthOnFile } from "../../../../singleOrderLayout/utils/existingDocsToLs";
import { CandidUploadDropdownAction } from "../candidUploadActions";

/**
 * Builds modal openers and the Trigger Candid dropdown click handler from
 * the current order's coverages, auth-on-file J-codes, and auth status options.
 *
 * @returns Handler for dropdown menu action ids
 */
export const useCandidUploadActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { order, authOnFile, coverages } = useSingleOrderUnwrappedData();

  const primaryPayerName = useMemo(
    () =>
      coverages?.find((coverage) => coverage.coverage_type === "primary")
        ?.payer_name ?? "",
    [coverages],
  );

  const eligibleJCodes = useMemo(
    () =>
      getAllJCodesFromAuthOnFile(authOnFile ?? undefined)?.filter((jCode) =>
        CANDID_ELIGIBLE_AUTH_STATUSES.includes(
          jCode?.auth_status as MedicalPaOrdersAuthStatus,
        ),
      ) ?? [],
    [authOnFile],
  );

  /**
   * Opens the Auth Letter Upload modal seeded with eligible J-codes and
   * order-level identifiers (`orderId`, `orgId`, `mrn`).
   */
  const openAuthorizationUploadModal = useCallback(() => {
    const orgId = order?.org_id ?? getOrgIdForFetchExternalWorklist() ?? "";
    const orderId = order?.order_id ?? "";
    const payerName = primaryPayerName;

    dispatch(
      setOpenedModalId({
        id: ModalId.AUTH_LETTER_UPLOAD_MODAL,
        metaData: {
          authorizationData: groupJCodesByAuthorization(eligibleJCodes),
          orderId,
          orgId,
          mrn:
            order?.demographics?.patient_id ??
            order?.demographics?.patient_mrn ??
            "",
          payerName,
        },
      }),
    );
  }, [dispatch, order, primaryPayerName, eligibleJCodes]);

  /**
   * Opens the New Document Upload modal for Candid, seeded with order context
   * for the document POST.
   */
  const openDocumentUploadModal = useCallback(() => {
    dispatch(
      setOpenedModalId({
        id: ModalId.NEW_DOCUMENT_UPLOAD_MODAL,
        metaData: {
          title: "Candid Document Upload",
          orderId: order?.order_id ?? "",
          orgId: order?.org_id ?? getOrgIdForFetchExternalWorklist() ?? "",
          mrn:
            order?.demographics?.patient_id ??
            order?.demographics?.patient_mrn ??
            "",
        },
      }),
    );
  }, [dispatch, order]);

  /**
   * Opens the Patient Notes (Candid Notes Update) modal, seeded with order
   * context for the notes POST.
   */
  const openAuthNotesModal = useCallback(() => {
    dispatch(
      setOpenedModalId({
        id: ModalId.PATIENT_NOTES,
        metaData: {
          title: "Candid Notes Update",
          orderId: order?.order_id ?? "",
          orgId: order?.org_id ?? getOrgIdForFetchExternalWorklist() ?? "",
          mrn:
            order?.demographics?.patient_id ??
            order?.demographics?.patient_mrn ??
            "",
        },
      }),
    );
  }, [dispatch, order]);

  /**
   * Routes a Trigger Candid dropdown selection to the matching modal opener.
   *
   * @param action - Menu item id from {@link CandidUploadDropdownAction}
   */
  const handleDropdownActionClick = useCallback(
    (action: string) => {
      switch (action) {
        case CandidUploadDropdownAction.DocumentUpload:
          openDocumentUploadModal();
          break;
        case CandidUploadDropdownAction.AuthorizationInformation:
          openAuthorizationUploadModal();
          break;
        case CandidUploadDropdownAction.AuthNotes:
          openAuthNotesModal();
          break;
        default:
          break;
      }
    },
    [openDocumentUploadModal, openAuthorizationUploadModal, openAuthNotesModal],
  );

  return { handleDropdownActionClick };
};
